import { CARD_STATE } from './listingCardState';

export const STOP_OUTCOME = {
  SOLD: 'sold',
  OFFLINE: 'offline',
  CANCEL: 'cancel',
};

const PREFIX = 'StopListingModal';

/**
 * Which of the three ways to stop an advertisement still apply, and why the others
 * do not.
 *
 * Shown greyed with a reason rather than hidden. The menu hides actions that do not
 * apply, because a menu is a list of things you can do — but this dialog is a
 * comparison of three consequences, and removing one silently changes what the
 * remaining two are being compared against. "Already offline" is also simply more
 * useful than an option that was never there.
 *
 * @param {Object} args
 * @param {string} args.state a CARD_STATE
 * @param {boolean} [args.hasCancelScheduled] the subscription already lapses on a date
 * @param {boolean} [args.isBusinessSeller] no per-listing subscription to cancel
 * @returns {Array<{ key, isAvailable, unavailableReasonId }>}
 */
export const deriveStopOptions = ({
  state,
  hasCancelScheduled = false,
  isBusinessSeller = false,
}) => {
  const isOffline = state === CARD_STATE.OFFLINE;

  return [
    // Always available: an advertisement can be sold whether it is online or not, and
    // this is the only option that ends the listing rather than pausing it.
    { key: STOP_OUTCOME.SOLD, isAvailable: true },

    {
      key: STOP_OUTCOME.OFFLINE,
      isAvailable: !isOffline,
      unavailableReasonId: `${PREFIX}.unavailable.alreadyOffline`,
    },

    // Business sellers never see this one at all — they have no per-listing
    // subscription — so it is filtered out rather than disabled.
    ...(isBusinessSeller
      ? []
      : [
          {
            key: STOP_OUTCOME.CANCEL,
            isAvailable: !isOffline && !hasCancelScheduled,
            // Cancelling is still reachable from "Abonnement beheren" in both cases,
            // and the reason says so — a seller who came here to stop paying must not
            // be left with every money-stopping option greyed out and no way on.
            unavailableReasonId: hasCancelScheduled
              ? `${PREFIX}.unavailable.alreadyCancelled`
              : `${PREFIX}.unavailable.offlineUseSubscription`,
          },
        ]),
  ];
};
